import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Checkbox } from '@/components/checkbox';
import { CollectionOptionType } from '@/hooks/use-collection-list-options';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  MULTI_ORDER_GUIDE_QUERY,
  ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT,
} from '@/contexts/items-context-queries';
import {
  OrderGuide_DuplicateCollectionMutation,
  OrderGuide_DuplicateCollectionMutationVariables,
} from '@/types';
import { OrderGuideActionSheetNameInput } from './order-guide-action-sheet-name-input';
import { StyleSheet } from 'react-native';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { validateCollectionName } from '@/utils/order-guide';

export const DUPLICATE_COLLECTION_MUTATION = gql`
  mutation OrderGuide_DuplicateCollection(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $collectionUUID: uuid!
    $collectionName: String!
    $duplicateParLevels: Boolean!
  ) {
    duplicateOrderGuideCollection(
      collection_id: $collectionUUID
      new_collection_name: $collectionName
      restaurant_id: $restaurantUUID
      should_duplicate_par_levels: $duplicateParLevels
      supplier_id: $supplierUUID
    ) {
      order_guide_restaurant_collection {
        ...ItemsContext_OrderGuideRestaurantCollectionFields
      }
    }
  }

  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

interface Props {
  inputError: string;
  onDuplicate: (newCollection: CollectionOptionType) => void;
  setInputError: React.Dispatch<React.SetStateAction<string>>;
  sourceCollection: CollectionOptionType;
}

export const DuplicateCollectionContent: React.FC<Props> = ({
  inputError,
  onDuplicate,
  setInputError,
  sourceCollection,
}) => {
  const [copyParLevels, setCopyParLevels] = useState(true);

  const orderGuideStoreApi = useOrderGuideStoreApi();
  const { selectedRestaurantUUID, selectedRestaurantRelationship } =
    useUserContext();
  const { showErrorToast } = useToasts();

  const [duplicateCollection, { loading }] = useMutation<
    OrderGuide_DuplicateCollectionMutation,
    OrderGuide_DuplicateCollectionMutationVariables
  >(DUPLICATE_COLLECTION_MUTATION, {
    // Refetch to get the duplicated items (the mutation only returns the collection)
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(MULTI_ORDER_GUIDE_QUERY) as string],
  });

  const { collections } = useOrderGuideItemsContext();

  return (
    <OrderGuideActionSheetNameInput
      containerStyle={styles.inputContainer}
      initialName={i18n.t('copy_of', {
        collectionName: sourceCollection.title,
      })}
      inputError={inputError}
      isSaving={loading}
      nameInputLabel={i18n.t('order_guide_name')}
      onChangeText={() => setInputError('')}
      onSave={async (name) => {
        if (
          !validateCollectionName(
            name,
            collections.map((c) => c.name),
          )
        ) {
          setInputError(i18n.t('order_guide_name_error'));
          return;
        }
        try {
          const result = await duplicateCollection({
            variables: {
              collectionName: name,
              collectionUUID: String(sourceCollection.value),
              duplicateParLevels: copyParLevels,
              restaurantUUID: selectedRestaurantUUID,
              supplierUUID: selectedRestaurantRelationship?.supplierUUID,
            },
          });
          const collection =
            result.data?.duplicateOrderGuideCollection
              .order_guide_restaurant_collection;
          if (!collection) {
            throw new Error('no collection returned');
          }

          // Add the collection to the store (the duplicated items will come from refetch)
          orderGuideStoreApi.getState().addRestaurantCollection(collection);

          onDuplicate({
            metadata: {
              isDefault: collection.is_default,
              type: 'restaurant',
            },
            title: collection.name!,
            value: collection.uuid!,
          });
        } catch (e) {
          showErrorToast();
        }
      }}
      saveButtonText={i18n.t('create')}
    >
      <Checkbox
        isSelected={copyParLevels}
        label={i18n.t('duplicate_par_levels')}
        onSelect={() => {
          setCopyParLevels((prev) => !prev);
        }}
        style={[styles.checkbox, inputError ? styles.checkboxWithError : null]}
        trackingData={{
          name: 'duplicate_collection.copy_par_level',
          properties: {
            enabled: !copyParLevels,
          },
        }}
      />
    </OrderGuideActionSheetNameInput>
  );
};

const styles = StyleSheet.create({
  checkbox: {
    paddingBottom: 24,
    paddingHorizontal: 0,
    paddingTop: 32,
  },
  checkboxWithError: {
    paddingTop: 12,
  },
  inputContainer: {
    marginBottom: 0,
  },
});
